import { LucideIcon, User, Phone, Code, Users, CreditCard, Building2, Plug, Globe2, Activity, Gift } from "lucide-react";
import { AppRole } from "@/core/auth/roles";
import { P } from "@/core/auth/permissionRegistry";
import { FeatureStatusType } from "./featureStatus";

export interface TabConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  roles?: AppRole[] | 'all';
  permissions?: string[];
  requiresClient?: boolean;
  requiresOrg?: boolean;
  description?: string;
  group: 'personal' | 'client' | 'integrations' | 'admin';
  /** Feature status for Coming Soon/Beta badges */
  status?: FeatureStatusType;
  /** Feature key for ComingSoon wrapper lookup */
  featureKey?: string;
  /** Whether this tab is hidden (legacy routes still work) */
  hidden?: boolean;
}

/**
 * NEW 6-TAB STRUCTURE (Week 4 Consolidation)
 * 
 * Account - Profile, preferences, password, 2FA (coming soon)
 * Company - Branding (logo, colors), business info (industry, timezone)
 * Communications - Phone numbers, Twilio configuration, SMS templates
 * Integrations - Overview | CRM (coming soon) | Zapier | Mail Provider | API Keys
 * Team - Members | Pending Invitations | Roles & Permissions
 * Billing (Coming Soon) - Usage metrics, invoices, payment methods
 */
export const settingsTabs: TabConfig[] = [
  // ===================
  // PERSONAL SETTINGS
  // ===================
  {
    id: 'account',
    label: 'Account',
    icon: User,
    roles: 'all',
    description: 'Profile, preferences, password, security',
    group: 'personal'
  },
  {
    id: 'gift-cards',
    label: 'My Gift Cards',
    icon: Gift,
    roles: 'all',
    permissions: [P.GIFT_CARDS_VIEW],
    description: 'View your gift cards',
    group: 'personal'
  },
  
  // ===================
  // CLIENT SETTINGS  
  // ===================
  {
    id: 'company',
    label: 'Company',
    icon: Building2,
    roles: ['admin', 'tech_support', 'agency_owner', 'company_owner'],
    permissions: [P.SETTINGS_GENERAL],
    description: 'Business info, branding, and logo',
    group: 'client'
  },
  {
    id: 'communications',
    label: 'Communications',
    icon: Phone,
    roles: ['admin', 'tech_support', 'agency_owner', 'company_owner'],
    permissions: [P.SETTINGS_GENERAL],
    description: 'Phone numbers and Twilio configuration',
    group: 'client'
  },
  
  {
    id: 'gift-card-inventory',
    label: 'Gift Card Inventory',
    icon: Gift,
    roles: ['admin', 'tech_support', 'agency_owner', 'company_owner'],
    permissions: [P.GIFT_CARDS_MANAGE_POOLS],
    description: 'Manage available gift cards for campaigns',
    group: 'client'
  },
  {
    id: 'domains',
    label: 'Domains',
    icon: Globe2,
    roles: ['admin', 'tech_support', 'agency_owner', 'company_owner'],
    permissions: [P.DOMAINS_VIEW],
    requiresClient: true,
    description: 'Custom domains for branded emails and forms',
    group: 'client'
  },

  // ===================
  // INTEGRATIONS
  // ===================
  {
    id: 'integrations',
    label: 'Integrations',
    icon: Plug,
    roles: ['admin', 'agency_owner', 'company_owner', 'developer'],
    permissions: [P.SETTINGS_INTEGRATIONS],
    description: 'CRM, Zapier, Mail Provider, API',
    group: 'integrations'
  },
  {
    id: 'api-docs',
    label: 'API Docs',
    icon: Code,
    roles: ['admin', 'developer'],
    permissions: [P.API_VIEW],
    description: 'API documentation and reference',
    group: 'integrations'
  },
  
  // ===================
  // ADMINISTRATION
  // ===================
  {
    id: 'team',
    label: 'Team',
    icon: Users,
    roles: ['admin', 'agency_owner', 'company_owner'],
    permissions: [P.USERS_MANAGE],
    description: 'Team members, invitations, and roles',
    group: 'admin'
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: CreditCard,
    roles: ['admin', 'agency_owner', 'company_owner'],
    permissions: [P.SETTINGS_BILLING],
    description: 'Usage, invoices, and payment methods',
    group: 'admin',
    status: 'coming_soon',
    featureKey: 'billing'
  },
  {
    id: 'activity',
    label: 'Activity & Logs',
    icon: Activity,
    roles: ['admin', 'tech_support', 'agency_owner', 'company_owner'],
    permissions: [P.MONITORING_VIEW],
    description: 'Unified activity logs, alerts, and monitoring',
    group: 'admin'
  }
];

/** Get visible tabs (excluding hidden legacy tabs) */
export const getVisibleTabs = () => settingsTabs.filter(tab => !tab.hidden);

/** Legacy route redirects */
export const legacyRouteRedirects: Record<string, string> = {
  'general': 'company',
  'branding': 'company',
  'phone': 'communications',
  'sms': '/activity?tab=gift-cards', // External redirect
  'crm': 'integrations?tab=crm',
  'zapier': 'integrations?tab=zapier',
  'mail-provider': 'integrations?tab=mail-provider',
  'api': 'integrations?tab=api',
  'users': 'team',
  'security': 'account', // Security settings moved to Account
  'activity': '/monitoring',
  'api-docs': '/api-docs',
  'gift-cards': '/client/gift-cards',
};

export const settingsGroups = [
  { id: 'personal', label: 'Personal', description: 'Your account settings' },
  { id: 'client', label: 'Client Settings', description: 'Organization and branding' },
  { id: 'integrations', label: 'Integrations', description: 'Connect external services' },
  { id: 'admin', label: 'Administration', description: 'Users and billing' }
] as const;
